import { useEffect, useRef, useState } from "react";
import api, { mediaSrc } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Upload, ImageOff, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";

export function MediaPickerDialog({ open, onOpenChange, onSelect }) {
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/media").then((r) => setMedia(r.data)).catch(() => {});
  useEffect(() => { if (open) load(); }, [open]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/media/upload", fd);
      toast.success("התמונה הועלתה");
      await load();
      onSelect(r.data.url);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "העלאה נכשלה");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="font-editor max-w-2xl" data-testid="media-picker-dialog">
        <DialogHeader>
          <DialogTitle className="text-right">בחירת תמונה</DialogTitle>
        </DialogHeader>
        <div>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={upload} data-testid="media-picker-file" />
          <button onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="w-full mb-5 flex items-center justify-center gap-2 border-2 border-dashed border-[#E5E2D9] rounded-xl py-6 text-[#5C635C] hover:border-[#4A5D4E] hover:text-[#4A5D4E] transition-colors">
            <Upload className="w-5 h-5" />
            {uploading ? "מעלה..." : "העלאת תמונה חדשה (JPG, PNG, WEBP · עד 8MB)"}
          </button>
          {media.length === 0 ? (
            <div className="text-center py-10 text-[#8a8f89]">
              <ImageOff className="w-8 h-8 mx-auto mb-2" />
              עדיין אין תמונות בספרייה
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3 max-h-80 overflow-y-auto">
              {media.map((m) => (
                <button key={m.id} onClick={() => { onSelect(m.url); onOpenChange(false); }}
                        data-testid={`media-pick-${m.id}`}
                        className="group relative aspect-square rounded-lg overflow-hidden border border-[#E5E2D9] hover:ring-2 hover:ring-[#4A5D4E]">
                  <img src={mediaSrc(m.url)} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-[#4A5D4E]/0 group-hover:bg-[#4A5D4E]/20 flex items-center justify-center transition-colors">
                    <Check className="w-6 h-6 text-white opacity-0 group-hover:opacity-100" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function MediaField({ value, onChange, help }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      {value ? (
        <div className="relative inline-block">
          <img src={mediaSrc(value)} alt="" className="w-40 h-28 object-cover rounded-lg border border-[#E5E2D9]" />
          <div className="absolute -top-2 -left-2 flex gap-1">
            <button type="button" onClick={() => setOpen(true)} className="bg-white border border-[#E5E2D9] rounded-full p-1.5 shadow-sm hover:bg-[#F7F5F0]" title="החלפה">
              <Upload className="w-3.5 h-3.5 text-[#4A5D4E]" />
            </button>
            <button type="button" onClick={() => onChange("")} className="bg-white border border-[#E5E2D9] rounded-full p-1.5 shadow-sm hover:bg-[#F7F5F0]" title="הסרה" data-testid="media-remove">
              <Trash2 className="w-3.5 h-3.5 text-[#8C3A3A]" />
            </button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => setOpen(true)} data-testid="media-choose"
                className="flex items-center gap-2 border-2 border-dashed border-[#E5E2D9] rounded-lg px-5 py-4 text-[#5C635C] hover:border-[#4A5D4E] hover:text-[#4A5D4E] transition-colors">
          <Upload className="w-4 h-4" /> בחירת / העלאת תמונה
        </button>
      )}
      {help && <p className="text-xs text-[#8a8f89] mt-2">{help}</p>}
      <MediaPickerDialog open={open} onOpenChange={setOpen} onSelect={onChange} />
    </div>
  );
}
